import { baseUrl, log, debug, sessionId } from './common'
import type { NotificationJson } from '../gen/creativefabrica/notifications/v1/types_pb'
import {
  setNotificationsStoreState,
  routeNotificationToStore,
  lastEvent,
  hasSubscriptionsForToken,
} from './notifications'
import { getToken, getCurrentToken } from '@repo/rpc-codegen/fetch-rpc'

export function getNotificationSSEUrl(sessionId: string, token: string): string {
  return `${baseUrl}/notifications?sessionId=${sessionId}&token=${token}`
}

interface EventSourceEntry {
  eventSource: EventSource
  state: 'connecting' | 'connected' | 'disconnected'
  reconnectAttempts: number
  lastError: Event | null
  connectionStartTime: number | null
  healthCheckInterval: NodeJS.Timeout | null
  stateCheckInterval: NodeJS.Timeout | null
  token: string
}

const eventSourceMap = new Map<string, EventSourceEntry>()
function getEventSourceMapKey(token?: string): string {
  return `${sessionId}-${token}`
}
function getEventSourceEntry(token?: string): EventSourceEntry | undefined {
  return eventSourceMap.get(getEventSourceMapKey(token))
}

let reconnectTimeout: NodeJS.Timeout | null = null
let reconnectAttempts = 0

let healthCheckInterval: NodeJS.Timeout | null = null
let stateCheckInterval: NodeJS.Timeout | null = null

function patchEventSourceEntry(eventSourceEntry: Partial<EventSourceEntry> & { token: string }) {
  const current = getEventSourceEntry(eventSourceEntry.token)
  if (!current) {
    return null
  }
  const newEntry = {
    ...current,
    ...eventSourceEntry,
  }
  eventSourceMap.set(getEventSourceMapKey(eventSourceEntry.token), newEntry)
}

export async function connect(providedToken?: string) {
  const eventSourceEntry = getEventSourceEntry(providedToken)
  if (eventSourceEntry?.state === 'connecting' || eventSourceEntry?.state === 'connected') {
    return
  }

  try {
    let token = providedToken
    if (!token) {
      await getToken()
      token = getCurrentToken() || ''
    }
    const url = getNotificationSSEUrl(sessionId, token)
    const newEventSource = new EventSource(url)
    const newEventSourceEntry: EventSourceEntry = {
      eventSource: newEventSource,
      state: 'connecting',
      reconnectAttempts: 0,
      lastError: null,
      connectionStartTime: Date.now(),
      healthCheckInterval: null,
      stateCheckInterval: null,
      token,
    }
    eventSourceMap.set(getEventSourceMapKey(token), newEventSourceEntry)

    newEventSource.onopen = () => {
      debug && log('SSE connection established successfully')
      newEventSourceEntry.state = 'connected'
      newEventSourceEntry.connectionStartTime = Date.now()
      newEventSourceEntry.reconnectAttempts = 0
      patchEventSourceEntry(newEventSourceEntry)
      clearReconnectTimeout()
      updateStoreState()
    }

    newEventSource.onerror = event => {
      newEventSourceEntry.state = 'disconnected'
      newEventSourceEntry.lastError = event
      patchEventSourceEntry(newEventSourceEntry)
      updateStoreState()

      if (newEventSource) {
        newEventSource.close()
      }

      if (
        event.type === 'error' &&
        newEventSource.readyState === EventSource.CLOSED &&
        !hasSubscriptionsForToken(token)
      ) {
        return
      }
      attemptReconnect(token)
    }

    newEventSource.addEventListener('notification', event => {
      try {
        const data = JSON.parse(event.data) as NotificationJson
        debug && log('Received notification:', data)
        updateStoreState()
        routeNotificationToStore(data)
        // oxlint-disable-next-line unicorn/catch-error-name
      } catch (err) {
        debug && log('Failed to parse notification', err)
      }
    })

    startConnectionHealthCheck(token)
    // oxlint-disable-next-line unicorn/catch-error-name
  } catch (err) {
    debug && log('Failed to create EventSource:', err)
    const eventSourceEntry = getEventSourceEntry(providedToken)
    if (eventSourceEntry?.eventSource) {
      eventSourceEntry.state = 'disconnected'
      eventSourceEntry.lastError = err as Event
      patchEventSourceEntry(eventSourceEntry)
      eventSourceEntry.eventSource.close()
    }

    updateStoreState()
    attemptReconnect(providedToken)
  }
}

function startConnectionHealthCheck(providedToken?: string) {
  healthCheckInterval = setInterval(() => {
    const eventSourceEntry = getEventSourceEntry(providedToken)
    if (
      !eventSourceEntry?.eventSource ||
      eventSourceEntry.eventSource.readyState === EventSource.CLOSED
    ) {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval)
        healthCheckInterval = null
      }
      attemptReconnect(providedToken)
      return
    }
  }, 30_000)

  monitorConnectionState(providedToken)
}

function monitorConnectionState(providedToken?: string) {
  stateCheckInterval = setInterval(() => {
    const eventSourceEntry = getEventSourceEntry(providedToken)
    if (!eventSourceEntry?.eventSource) {
      if (stateCheckInterval) {
        clearInterval(stateCheckInterval)
        stateCheckInterval = null
      }
      return
    }

    const currentState = eventSourceEntry.eventSource.readyState

    // If the connection was open but is now closed, attempt to reconnect
    if (currentState === EventSource.CLOSED && getIsConnected(eventSourceEntry.eventSource)) {
      debug && log('Connection state changed to CLOSED, attempting reconnect')
      attemptReconnect(providedToken)
      if (stateCheckInterval) {
        clearInterval(stateCheckInterval)
        stateCheckInterval = null
      }
      return
    }

    if (currentState === EventSource.CONNECTING && eventSourceEntry.state === 'connecting') {
      const connectionTimeout = 10_000
      if (Date.now() - eventSourceEntry.connectionStartTime! > connectionTimeout) {
        debug && log('Connection timeout, attempting reconnect')
        eventSourceEntry.state = 'disconnected'
        patchEventSourceEntry(eventSourceEntry)
        attemptReconnect(providedToken)
        if (stateCheckInterval) {
          clearInterval(stateCheckInterval)
          stateCheckInterval = null
        }
        return
      }
    }
  }, 5000)
}

export function disconnect(providedToken?: string) {
  const eventSourceEntry = getEventSourceEntry(providedToken)
  if (!eventSourceEntry?.eventSource) {
    return
  }

  debug && log('Disconnecting')
  clearReconnectTimeout()
  clearHealthCheckInterval()
  clearStateCheckInterval()
  reconnectAttempts = 0
  eventSourceEntry.lastError = null

  if (eventSourceEntry.eventSource) {
    eventSourceEntry.eventSource.close()
  }
  eventSourceEntry.state = 'disconnected'
  patchEventSourceEntry(eventSourceEntry)
  updateStoreState()
}

function clearReconnectTimeout() {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout)
    reconnectTimeout = null
  }
}

function clearHealthCheckInterval() {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval)
    healthCheckInterval = null
  }
}

function clearStateCheckInterval() {
  if (stateCheckInterval) {
    clearInterval(stateCheckInterval)
    stateCheckInterval = null
  }
}

function attemptReconnect(providedToken?: string) {
  const maxReconnectAttempts = 5
  const baseReconnectInterval = 1000

  if (reconnectAttempts >= maxReconnectAttempts) {
    debug && log('Max reconnection attempts reached')
    return
  }

  const eventSourceEntry = getEventSourceEntry(providedToken)
  // Don't attempt to reconnect if we're already connecting
  if (eventSourceEntry?.state === 'connecting') {
    return
  }

  // No active `subscribeToEvent` entries for this JWT — usually because every
  // hook unsubscribed (navigation, dependency churn), not because auth is missing.
  if (!hasSubscriptionsForToken(providedToken)) {
    debug &&
      log(
        'Skipping reconnect: no active notification event subscriptions for this token (auth token may still be present)',
      )
    return
  }

  clearReconnectTimeout()

  // Exponential backoff: 1s, 2s, 4s, 8s, 16s
  const reconnectDelay = Math.min(
    baseReconnectInterval * Math.pow(2, reconnectAttempts),
    30_000, // Cap at 30 seconds
  )

  reconnectTimeout = setTimeout(() => {
    reconnectAttempts += 1
    debug &&
      log(
        `Attempting reconnect ${reconnectAttempts}/${maxReconnectAttempts} in ${reconnectDelay}ms`,
      )
    connect(providedToken)
  }, reconnectDelay)
}

function updateStoreState() {
  setNotificationsStoreState({
    lastEvent: getLastEvent(),
  })
}

function getIsConnected(eventSource: EventSource): boolean {
  return eventSource?.readyState === EventSource.OPEN
}

function getLastEvent(): NotificationJson | null {
  return lastEvent
}

export function getReconnectAttemptsCount(): number {
  return reconnectAttempts
}

export function resetReconnectAttempts() {
  reconnectAttempts = 0
}

export function isReconnecting(): boolean {
  return reconnectTimeout !== null
}

export function getEventSourceMap() {
  return eventSourceMap
}

export function isConnectedForToken(token?: string): boolean {
  const eventSourceEntry = getEventSourceEntry(token)
  return (
    eventSourceEntry?.state === 'connected' &&
    eventSourceEntry?.eventSource?.readyState === EventSource.OPEN
  )
}

export function getErrorForToken(token?: string): Event | null {
  const eventSourceEntry = getEventSourceEntry(token)
  return eventSourceEntry?.lastError || null
}

export function getReconnectAttemptsForToken(token?: string): number {
  const eventSourceEntry = getEventSourceEntry(token)
  return eventSourceEntry?.reconnectAttempts || 0
}

export const sseConnectionManager = {
  connect,
  disconnect,
  getReconnectAttemptsCount: getReconnectAttemptsCount,
  resetReconnectAttempts,
  isReconnecting,
}
